import {
  useState, useCallback, useEffect, useRef,
} from 'react'

import useApiDelete from 'Pods/Shared/Hooks/useApiDelete'
import useApiGet from 'Pods/Shared/Hooks/useApiGet'
import useApiPost from 'Pods/Shared/Hooks/useApiPost'
import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'
import { setOpenedModelCommentsCount } from 'Pods/ThreeDStudio/Store/actions/ui'

import {
  commentsPath, commentPath, COMMENT_QUOTA_PATTERN, COMMENTS_PER_PAGE,
  buildPendingCommentId, isPendingCommentId,
} from '../constants'
import { addReplyToTree, removeCommentFromTree, replaceCommentInTree } from '../Utils/commentTree'
import { normalizeComment, normalizeCommentsList } from '../Utils/normalizeComments'

/**
 * Owns the comment tree for the opened model: loads the first page when the
 * modal opens, appends further pages on demand (infinite scroll) and exposes
 * optimistic add / reply / delete. Root comments are paginated by the API;
 * replies arrive nested inside each root and are never paginated.
 * @param {{ slug: string, isOpen: boolean, syncOpenedModel?: boolean }} params - the image
 *   modal passes syncOpenedModel: false, since the count it loads is an image run's and
 *   would overwrite the opened model's footer badge
 */
export default function useModelComments({ slug, isOpen, syncOpenedModel = true }) {
  const pageConfig = usePageConfig()
  const { getData, isLoading: isFetching } = useApiGet({ initialLoading: true })
  const { postData } = useApiPost()
  const { deleteData } = useApiDelete()

  const [comments, setComments] = useState([])
  // Replies included, straight from the API - the modal header and the footer
  // badge must show the same number, and neither can be derived from the loaded
  // page (replies are nested, and root comments are paginated).
  const [totalCommentsCount, setTotalCommentsCount] = useState(null)
  // Root comments only (meta.totalItemsCount) - the number pagination runs on.
  // Never compare against commentsCount: replies inflate it, which would keep
  // hasMore true and fetch empty pages forever.
  const [totalRootCount, setTotalRootCount] = useState(null)
  const [perPage, setPerPage] = useState(COMMENTS_PER_PAGE)
  const [loadedPage, setLoadedPage] = useState(1)
  const [loadFailed, setLoadFailed] = useState(false)
  const [submitFailed, setSubmitFailed] = useState(false)
  const [limitReached, setLimitReached] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Every request callback compares against this to drop responses that belong to
  // a model the user has already navigated away from. Kept in an effect declared
  // before the fetch effect, so a slug change refreshes it first and the fetch it
  // triggers is never mistaken for the outgoing model's.
  const slugRef = useRef(slug)

  useEffect(() => {
    slugRef.current = slug
  }, [slug])

  const clearTransientState = useCallback(() => {
    setLoadFailed(false)
    setSubmitFailed(false)
    setLimitReached(false)
    setIsSubmitting(false)
  }, [])

  // The index/create/destroy responses all carry meta.commentsCount (all
  // comments, replies included) so the header and the footer badge stay on the
  // same number without a page refresh, plus meta.totalItemsCount (root
  // comments only) so hasMore stays accurate after mutations. perPage arrives
  // on index responses only.
  const syncCounts = useCallback((response) => {
    const meta = response?.data?.meta
    const count = meta?.commentsCount

    if (typeof count === 'number') {
      setTotalCommentsCount(count)
    }

    if (syncOpenedModel) {
      setOpenedModelCommentsCount(count)
    }

    if (typeof meta?.totalItemsCount === 'number') {
      setTotalRootCount(meta.totalItemsCount)
    }

    if (typeof meta?.perPage === 'number' && meta.perPage > 0) {
      setPerPage(meta.perPage)
    }
  }, [syncOpenedModel])

  const fetchComments = useCallback(() => {
    if (!slug) {
      return
    }

    const requestSlug = slug
    setLoadFailed(false)

    getData({
      path: commentsPath(slug),
      onSuccess: (response) => {
        if (slugRef.current !== requestSlug) {
          return
        }

        setLoadedPage(1)
        setComments(normalizeCommentsList(response.data))
        syncCounts(response)
      },
      onError: () => {
        if (slugRef.current !== requestSlug) {
          return
        }

        setLoadFailed(true)
      },
    })
  }, [slug, getData, syncCounts])

  // Page math, deliberately not a loaded-length comparison: DESC offset drift
  // can lose rows to the dedupe below, and a length that can never reach
  // totalRootCount would keep hasMore true and fetch pages forever once the
  // user hits the end of the list.
  const hasMore = totalRootCount !== null && loadedPage < Math.ceil(totalRootCount / perPage)

  // Appends the next page of root comments. Deduped by id because a comment
  // created between two fetches shifts the DESC offsets, making the next page
  // re-serve the previous page's last row. Not re-armed after a failure -
  // loadFailed stops the observer-driven loop from hammering a failing API, and
  // it clears on modal close / model switch like the other transient flags.
  const loadMore = useCallback(() => {
    if (!slug || isFetching || loadFailed || !hasMore) {
      return
    }

    const requestSlug = slug
    const nextPage = loadedPage + 1

    getData({
      path: commentsPath(slug),
      params: { page: nextPage },
      onSuccess: (response) => {
        if (slugRef.current !== requestSlug) {
          return
        }

        setLoadedPage(nextPage)
        const fetched = normalizeCommentsList(response.data)

        setComments((prev) => {
          const loadedIds = new Set(prev.map(comment => comment.id))

          return [...prev, ...fetched.filter(comment => !loadedIds.has(comment.id))]
        })
        syncCounts(response)
      },
      onError: () => {
        if (slugRef.current !== requestSlug) {
          return
        }

        setLoadFailed(true)
      },
    })
  }, [slug, getData, isFetching, loadFailed, hasMore, loadedPage, syncCounts])

  // The modal stays mounted across models, so a slug change has to drop the
  // previous tree - otherwise model A's comments render under model B until the
  // fetch lands, and a failed fetch leaves them on screen for good because the
  // load error only renders on an empty list.
  useEffect(() => {
    setComments([])
    setTotalCommentsCount(null)
    setTotalRootCount(null)
    setLoadedPage(1)
    clearTransientState()
  }, [slug, clearTransientState])

  useEffect(() => {
    if (isOpen && slug) {
      fetchComments()
    }
  }, [isOpen, slug, fetchComments])

  // The modal stays mounted while closed, so clear any load/submit error on close
  // - otherwise a stale "something went wrong" banner greets the next open.
  useEffect(() => {
    if (!isOpen) {
      clearTransientState()
    }
  }, [isOpen, clearTransientState])

  const buildOptimisticNode = useCallback((content) => ({
    id: buildPendingCommentId(),
    content,
    createdAt: new Date().toISOString(),
    author: {
      id: pageConfig?.currentUserId ?? null,
      username: pageConfig?.currentUserUsername || '',
      avatarUrl: pageConfig?.currentUserAvatarUrl || null,
      isAuthor: false,
    },
    replies: [],
  }), [pageConfig])

  // Resolves true once the server has accepted the comment, false when it was
  // rejected (quota, validation, network) - never rejects, so callers can await
  // it without an error handler. The composers keep the user's draft until they
  // see a true, so a rejected post can be retried without retyping.
  const postComment = useCallback(({ content, parentId, applyOptimistic }) => {
    const trimmed = content.trim()

    if (!trimmed || !slug) {
      return Promise.resolve(false)
    }

    const optimistic = buildOptimisticNode(trimmed)
    const requestSlug = slug
    setSubmitFailed(false)
    setLimitReached(false)
    setIsSubmitting(true)
    setComments(applyOptimistic(optimistic))

    return new Promise((resolve) => {
      postData({
        path: commentsPath(slug),
        data: {
          comment: {
            content: trimmed,
            ...(parentId && { parent_id: parentId }),
          },
        },
        onSuccess: (response) => {
          if (slugRef.current !== requestSlug) {
            resolve(false)

            return
          }

          setIsSubmitting(false)

          // Adopt the persisted node in place: it carries the server id (and the
          // canonical author payload), so Delete and Reply work on it. No page-1
          // refetch here - that would collapse every loaded page back to the
          // first 20 root comments.
          const persisted = normalizeComment(response.data?.data)

          if (persisted) {
            setComments(prev => replaceCommentInTree(prev, optimistic.id, persisted))
          }

          syncCounts(response)
          resolve(true)
        },
        onError: (error) => {
          if (slugRef.current !== requestSlug) {
            resolve(false)

            return
          }

          setComments(prev => removeCommentFromTree(prev, optimistic.id))
          setIsSubmitting(false)

          const details = error?.response?.data?.errors ?? []
          const isQuota = details.some(detail => COMMENT_QUOTA_PATTERN.test(detail?.detail || ''))

          if (isQuota) {
            setLimitReached(true)
          } else {
            setSubmitFailed(true)
          }

          resolve(false)
        },
      })
    })
  }, [slug, postData, buildOptimisticNode, syncCounts])

  const addComment = useCallback((content) => (
    postComment({ content, applyOptimistic: optimistic => prev => [optimistic, ...prev] })
  ), [postComment])

  const addReply = useCallback((parentId, content) => (
    postComment({
      content,
      parentId,
      applyOptimistic: optimistic => prev => addReplyToTree(prev, parentId, optimistic),
    })
  ), [postComment])

  const deleteComment = useCallback((commentId) => {
    // A pending node has no server id yet, so DELETE would 404 and the rollback
    // would restore the very node the in-flight create is about to replace.
    if (!slug || isPendingCommentId(commentId)) {
      return
    }

    const requestSlug = slug
    // Deleting a root while pages are still outstanding strands comments two ways:
    // every unloaded row shifts one offset up, so the next page fetch skips one,
    // and a total that drops into the loaded window closes hasMore with fewer rows
    // rendered than the server holds. Neither is recoverable without reopening the
    // modal, so re-anchor pagination from page 1 - the same trade the error path
    // below takes. Replies never move the root offsets, and with every page loaded
    // the optimistic removal is already the final state, so both skip the refetch.
    const strandsPages = hasMore && comments.some(comment => comment.id === commentId)

    setSubmitFailed(false)
    setLimitReached(false)
    setComments(prev => removeCommentFromTree(prev, commentId))

    deleteData({
      path: commentPath(slug, commentId),
      onSuccess: (response) => {
        if (slugRef.current !== requestSlug) {
          return
        }

        syncCounts(response)

        if (strandsPages) {
          fetchComments()
        }
      },
      onError: () => {
        if (slugRef.current !== requestSlug) {
          return
        }

        // Refetch instead of replaying a pre-delete snapshot: another add or
        // delete may have completed while this DELETE was in flight, and the
        // stale tree would silently drop it. The server settles it either way.
        // This resets pagination to page 1 - an accepted trade on a rare error
        // path; scrolling loads the later pages again.
        setSubmitFailed(true)
        fetchComments()
      },
    })
  }, [slug, deleteData, syncCounts, fetchComments, comments, hasMore])

  return {
    comments,
    totalCommentsCount,
    hasMore,
    loadMore,
    isLoading: isFetching,
    loadFailed,
    submitFailed,
    limitReached,
    isSubmitting,
    addComment,
    addReply,
    deleteComment,
    refetch: fetchComments,
  }
}
